import { Injectable } from '@angular/core';
import { State, Action, Selector, StateContext } from '@ngxs/store';
import { CompartmentRuleListDownloadStart, CompartmentRuleListInitialState } from './compartment-rule-list.actions';
import {
  LIST_SERVER_PAGINATION_INITIAL_STATE,
  ListServerPaginationStateModel,
} from '../../../../../../../common/models';
import { catchError, map, take } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { NotificationService } from '../../../../../../../common/services/notification/notification.service';
import { CompartmentRuleListService } from './compartment-rule-list.service';
import { CompartmentRuleListItem } from '../../../models/compartment-rule-list.model';
import { CompartmentRuleListLoad } from './compartment-rule-list.actions';
import { CustomHttpErrorResponse, Error } from '../../../../../../../common/constants/error';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';
import { NavigateUserService } from '../../../../../../services/navigate-user.service';

export type CompartmentRuleListStateModel = ListServerPaginationStateModel<CompartmentRuleListItem>;

const initialState: CompartmentRuleListStateModel = LIST_SERVER_PAGINATION_INITIAL_STATE;

@State<CompartmentRuleListStateModel>({
  name: 'compartmentRuleList',
  defaults: initialState,
})
@Injectable()
export class CompartmentRuleListState {
  constructor(
    private compartmentRuleListService: CompartmentRuleListService,
    private notificationService: NotificationService,
    private navigateUserService: NavigateUserService,
  ) {}

  @Selector()
  static getState(state: CompartmentRuleListStateModel): CompartmentRuleListStateModel {
    return state;
  }

  @Selector()
  static getItems(state: CompartmentRuleListStateModel): CompartmentRuleListItem[] {
    return state.items;
  }

  @Selector()
  static getLoaded(state: CompartmentRuleListStateModel): boolean {
    return state.loaded;
  }

  @Action(CompartmentRuleListLoad)
  getRuleList(
    ctx: StateContext<CompartmentRuleListStateModel>,
    { payload }: CompartmentRuleListLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.compartmentRuleListService.getCompartmentRuleList(payload).pipe(
      take(1),
      map((list: ListServerPaginationStateModel<CompartmentRuleListItem>) => {
        ctx.patchState({ ...list, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(CompartmentRuleListDownloadStart)
  downloadCompartmentRuleList(
    ctx: StateContext<CompartmentRuleListStateModel>,
    { payload: { dialogRef, navigationNeeded, ...body } }: CompartmentRuleListDownloadStart,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.compartmentRuleListService.downloadCompartmentRulesList(body).pipe(
      take(1),
      map(() => {
        this.notificationService.success('api.export.success.exportStart');
        dialogRef.close();
        if (navigationNeeded) {
          this.navigateUserService.navigateUserToAbsolutePath('/dashboard/exports');
        }
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(CompartmentRuleListInitialState)
  setInitialState(ctx: StateContext<CompartmentRuleListStateModel>): void {
    ctx.setState(initialState);
  }
}
